import type { DesktopMachine, DesktopModelTotal } from './desktop-types';
import { cardName, modelName, modelTone } from './gpu-words';
import type { GpuModel } from './gpu-types';

/** A model's Cortexa name; one the screen does not know yet under its own name. */
export function anyModelName(model: string): string {
  return model in modelName
    ? modelName[model as GpuModel]
    : model.charAt(0).toUpperCase() + model.slice(1);
}

/** The colour family a model shares with the GPU cards; others the accent. */
export function anyModelTone(model: string): string {
  return model in modelTone ? modelTone[model as GpuModel] : 'other';
}

/** The headline word for a machine. */
export function stateWord(m: DesktopMachine): string {
  if (m.state === 'offline') return 'Offline';
  if (m.state === 'paused') return 'Paused at the desk';
  return m.working.length > 0 ? 'Working' : 'Online, idle';
}

/** Counts for the strip on top: online, paused, offline. */
export function countsOf(machines: DesktopMachine[]): Record<DesktopMachine['state'], number> {
  return {
    online: machines.filter((m) => m.state === 'online').length,
    paused: machines.filter((m) => m.state === 'paused').length,
    offline: machines.filter((m) => m.state === 'offline').length,
  };
}

/** Each model's share of a span's calls, 0..1, largest first. */
export function sharesOf(
  totals: DesktopModelTotal[],
): { model: string; share: number; calls: number }[] {
  const all = totals.reduce((n, t) => n + t.calls, 0);
  return totals
    .filter((t) => t.calls > 0)
    .map((t) => ({ model: t.model, calls: t.calls, share: all === 0 ? 0 : t.calls / all }))
    .sort((a, b) => b.calls - a.calls);
}

/** Machines a filter keeps: by machine, card or model name, and by state. */
export function matching(
  machines: DesktopMachine[],
  text: string,
  state: DesktopMachine['state'] | 'all',
): DesktopMachine[] {
  const q = text.trim().toLowerCase();
  return machines.filter(
    (m) =>
      (state === 'all' || m.state === state) &&
      (!q ||
        [m.machine, m.displayName ?? '', m.card, ...m.models.map(anyModelName)].some((s) =>
          s.toLowerCase().includes(q),
        )),
  );
}

/** What people call a machine: its given name, else the machine's own. */
export function nameOf(m: DesktopMachine): string {
  return m.displayName?.trim() || m.machine;
}

/** A chip's label: the name and the card, "Dustin's PC · RTX 4090". */
export function chipLabel(m: DesktopMachine): string {
  return m.card ? `${nameOf(m)} · ${cardName(m.card)}` : nameOf(m);
}

/** What a machine runs, in Cortexa's words: "Understanding", "Page reader, Reasoner"; or that nothing is placed. */
export function runsWords(m: DesktopMachine): string {
  return m.models.length === 0 ? 'nothing placed' : m.models.map(anyModelName).join(', ');
}

/** How long a machine's last failure stays on its tile; the record itself is kept. */
export const FAILURE_SHOWN_MS = 24 * 60 * 60 * 1000;

/**
 * Whether a machine's last failure is recent enough to show. Nothing clears the record when the
 * machine works again, so an old one would sit on a healthy machine's tile for good.
 */
export function failureIsRecent(m: DesktopMachine, now: number = Date.now()): boolean {
  if (!m.lastError || !m.lastErrorAt) return false;
  return now - Date.parse(m.lastErrorAt) <= FAILURE_SHOWN_MS;
}
